import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, setAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const res = await axios.post(`${API}/admin/login`, formData);
      setAuthToken(res.data.token);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#4A5D23] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img 
            src="/logo.png"
            alt="Eden's Dawn"
            className="h-20 mx-auto mb-4 brightness-0 invert"
          />
          <p className="text-white/70">Admin Portal</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-hover p-8">
          <h2 className="font-heading text-2xl text-[#2C2C2C] mb-6 text-center">Sign In</h2>

          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Username</Label>
              <Input
                name="email"
                type="text"
                data-testid="admin-email-input"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="input-elegant"
                placeholder="Enter username"
              />
            </div>

            <div>
              <Label className="text-sm font-medium text-[#2C2C2C] mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="admin-password-input"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="input-elegant pr-10"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="admin-login-btn"
              disabled={isLoading}
              className="w-full bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full py-6"
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
